import React, { useEffect, useState } from "react";
import "./ProductModal.css"; // Ensure this CSS file is created and linked
import { toast, ToastContainer } from "react-toastify";
import { FaDownload, FaTimes, FaCopy } from "react-icons/fa";

const ProductModal = ({ isOpen, onClose, product }) => {
  const [zoomedImage, setZoomedImage] = useState(null);
  // Prevent scrolling on the underlying page when modal is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeZoom();
        onClose();
      }
    };

    const preventScroll = isOpen
      ? (document.body.style.overflow = "hidden")
      : (document.body.style.overflow = "visible");

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "visible"; // Ensure scrolling is re-enabled when the modal is closed
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "visible";
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const openZoom = (image) => {
    setZoomedImage(image);
    document.body.style.overflow = "hidden"; // Prevent scrolling on zoom
  };

  const closeZoom = () => {
    setZoomedImage(null);
    document.body.style.overflow = "visible"; // Allow scrolling after zoom out
  };

  const copyToClipboard = () => {
    const productDetails = `
*Brand Name*: ${product.brand_name}\n
*Composition*: ${product.compostion}\n
*Packing*: ${product.packing}\n
*MRP*: ₹${product.mrp}/- per ${product.unit}\n
*Category*: ${product.category}\n
*Company*: ${product.company}\n
*Indications*: ${product.description}\n
`;
    navigator.clipboard.writeText(productDetails).then(
      () => {
        toast.success("Product details copied to clipboard!", {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
      (err) => {
        toast.error("Failed to copy product details.", {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    );
  };

  // Function to download the image
  const downloadImage = (url, name) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", name); // Set the filename for download
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const getImagePath = (company, imageName) => {
    // Base URL for images in production - adjust the domain as necessary
    let basePath = "";

    // Determine the base path based on the company
    switch (company) {
      case "Trumac":
        basePath = "/app_admin/assets/images/products";
        break;
      case "Camrut":
        basePath = "/camrut_app_admin/assets/images/products";
        break;
      case "Medisync":
        basePath = "/medisync_app_admin/assets/images/products";
        break;
      case "Cialto Drugs":
        basePath = "/cialto_app_admin/assets/images/products";
        break;
      case "Pythus Pharma":
        basePath = "/pythus_app_admin/assets/images/products";
        break;
      default:
        basePath = "/images"; // Default path for development or fallback
    }

    // Check if the environment is development or production
    // This is a basic check, you might need to adjust it based on your setup
    const isDevelopment =
      !process.env.NODE_ENV || process.env.NODE_ENV === "development";

    // Return the correct path
    return isDevelopment
      ? `${process.env.PUBLIC_URL}/images/${imageName}`
      : `${basePath}/${imageName}`;
  };

  const getVisualPath = (company, imageName) => {
    // Base URL for images in production - adjust the domain as necessary
    let basePath = "";

    // Determine the base path based on the company
    switch (company) {
      case "Trumac":
        basePath = "/app_admin/assets/images/visualaid";
        break;
      case "Camrut":
        basePath = "/camrut_app_admin/assets/images/visualaid";
        break;
      case "Medisync":
        basePath = "/medisync_app_admin/assets/images/visualaid";
        break;
      case "Cialto Drugs":
        basePath = "/cialto_app_admin/assets/images/visualaid";
        break;
      case "Pythus Pharma":
        basePath = "/pythus_app_admin/assets/images/visualaid";
        break;
      default:
        basePath = "/images"; // Default path for development or fallback
    }

    // Check if the environment is development or production
    // This is a basic check, you might need to adjust it based on your setup
    const isDevelopment =
      !process.env.NODE_ENV || process.env.NODE_ENV === "development";

    // Return the correct path
    return isDevelopment
      ? `${process.env.PUBLIC_URL}/images/${imageName}`
      : `${basePath}/${imageName}`;
  };

  return (
    <>
      {zoomedImage && (
        <div className="zoom-overlay" onClick={closeZoom}>
          <img src={zoomedImage} alt="Zoomed" />
          <FaTimes className="zoom-close-icon" onClick={closeZoom} />
        </div>
      )}
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{product.brand_name}</h2>

            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="modal-body">
            <div className="product-image">
              <img
                src={getImagePath(product.company, product.product_image)}
                alt={product.brand_name}
                onClick={() =>
                  openZoom(getImagePath(product.company, product.product_image))
                }
              />
              <div
                className="download-icon"
                onClick={() =>
                  downloadImage(
                    getImagePath(product.company, product.product_image),
                    `${product.brand_name}.jpg`
                  )
                }
              >
                <FaDownload />
              </div>
            </div>
            <div className="product-info">
              <div className="brand-header">
                <h2 className="brand-name">{product.brand_name}</h2>
                <FaCopy className="copy-icon" onClick={copyToClipboard} />
              </div>
              <p>{product.compostion}</p>
              <p>
                <strong>Packing:</strong> {product.packing}
              </p>
              <p>
                <strong>MRP:</strong> ₹{product.mrp}/- per {product.unit}
              </p>
              <p>
                <strong>Category:</strong> {product.category}
              </p>
              <p>
                <strong>Company:</strong> {product.company}
              </p>
              <p>
                <strong>Indications:</strong> {product.description}
              </p>
            </div>
            {product.visual_aid && (
              <div className="visual-aid">
                <h3>Visual Aid</h3>
                <img
                  src={getVisualPath(product.company, product.visual_aid)}
                  alt="Visual Aid"
                  className="visual-aid-image"
                  onClick={() =>
                    openZoom(getVisualPath(product.company, product.visual_aid))
                  }
                />
                <div
                  className="download-icon"
                  onClick={() =>
                    downloadImage(
                      getVisualPath(product.company, product.visual_aid),
                      `${product.brand_name}-visual-aid.jpg`
                    )
                  }
                >
                  <FaDownload />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="bottom-center" />
    </>
  );
};

export default ProductModal;
